/**
 * Integrations Tab for Unified Dashboard
 * =======================================
 * Compact view of connected integrations with quick access to Integration Hub
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Check, X, RefreshCw, Loader2, ArrowRight,
  DollarSign, Users, BarChart3, Heart, CreditCard, Zap,
  Link2, AlertCircle, Clock, Activity
} from 'lucide-react';

// Smart backend URL detection
const getBackendUrl = () => {
  // Prefer REACT_APP_BACKEND_URL, but if it is the SAME site as the page
  // (host differs only by a www. prefix) use the page origin instead.
  // The deployed build baked 'https://www.corvana.com.au' while users load
  // the apex 'https://corvana.com.au'; www only 308-redirects to apex, which
  // breaks credentialed cross-origin CORS/CSP requests ('failed to fetch').
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (typeof window !== 'undefined') {
    if (envUrl) {
      try {
        const envHost = new URL(envUrl).hostname;
        const curHost = window.location.hostname;
        if (envHost.replace(/^www\./i, '') === curHost.replace(/^www\./i, '') && envHost !== curHost) {
          return window.location.origin;
        }
      } catch (e) { /* malformed env URL — fall through */ }
    }
    return envUrl || window.location.origin;
  }
  return envUrl || '';
};

const backendUrl = getBackendUrl();

// Integration categories with icons
const CATEGORIES = {
  accounting: { name: 'Accounting', icon: DollarSign, color: 'emerald' },
  workforce: { name: 'Workforce', icon: Users, color: 'purple' },
  crm: { name: 'CRM', icon: BarChart3, color: 'pink' },
  healthcare: { name: 'Healthcare', icon: Heart, color: 'red' },
  pos: { name: 'Payments', icon: CreditCard, color: 'indigo' },
  analytics: { name: 'Analytics', icon: Zap, color: 'amber' },
  scheduling: { name: 'Scheduling', icon: Clock, color: 'cyan' },
  inventory: { name: 'Inventory', icon: Activity, color: 'slate' },
  delivery: { name: 'Delivery', icon: Activity, color: 'green' },
  banking: { name: 'Banking', icon: DollarSign, color: 'blue' }
};

// Providers list - only include ones with /status endpoints
const PROVIDERS = [
  { id: 'xero', name: 'Xero', category: 'accounting', color: '#13B5EA' },
  { id: 'myob', name: 'MYOB', category: 'accounting', color: '#6B2D7B' },
  { id: 'quickbooks', name: 'QuickBooks', category: 'accounting', color: '#2CA01C' },
  { id: 'tanda', name: 'Tanda', category: 'workforce', color: '#00BFA5' },
  { id: 'deputy', name: 'Deputy', category: 'workforce', color: '#0066FF' },
  { id: 'hubspot', name: 'HubSpot', category: 'crm', color: '#FF7A59' },
  { id: 'salesforce', name: 'Salesforce', category: 'crm', color: '#00A1E0' },
  { id: 'pipedrive', name: 'Pipedrive', category: 'crm', color: '#00A651' },
  { id: 'zoho', name: 'Zoho CRM', category: 'crm', color: '#E42527' },
  { id: 'cliniko', name: 'Cliniko', category: 'healthcare', color: '#00A8E8' },
  { id: 'mindbody', name: 'Mindbody', category: 'healthcare', color: '#00D4FF' },
  { id: 'square', name: 'Square', category: 'pos', color: '#006AFF' },
  { id: 'stripe', name: 'Stripe', category: 'pos', color: '#635BFF' },
  { id: 'shopify', name: 'Shopify', category: 'pos', color: '#96BF48' },
  { id: 'lightspeed', name: 'Lightspeed', category: 'pos', color: '#E31937' },
  { id: 'google', name: 'Google', category: 'analytics', color: '#4285F4' },
  { id: 'meta', name: 'Meta Ads', category: 'analytics', color: '#1877F2' },
  { id: 'calendly', name: 'Calendly', category: 'scheduling', color: '#006BFF' },
  { id: 'cin7', name: 'Cin7', category: 'inventory', color: '#FF6B00' },
  { id: 'ubereats', name: 'Uber Eats', category: 'delivery', color: '#06C167' },
  { id: 'doordash', name: 'DoorDash', category: 'delivery', color: '#FF3008' },
  { id: 'basiq', name: 'Basiq', category: 'banking', color: '#00C7B7' }
];

const ProviderBadge = ({ provider, status }) => {
  const isConnected = status?.connected;
  const isConfigured = status?.configured;
  
  return (
    <div 
      className={`
        flex items-center gap-2 px-3 py-2 rounded-lg border transition-all
        ${isConnected 
          ? 'bg-emerald-50 border-emerald-200' 
          : isConfigured 
            ? 'bg-slate-50 border-slate-200 hover:border-slate-300'
            : 'bg-slate-100 border-slate-200 opacity-60'
        }
      `}
    >
      <div 
        className="w-6 h-6 rounded flex items-center justify-center text-white text-[10px] font-bold"
        style={{ backgroundColor: provider.color }}
      >
        {provider.name.substring(0, 2).toUpperCase()}
      </div>
      <span className="text-sm font-medium text-slate-700">{provider.name}</span>
      {isConnected ? (
        <Check className="w-4 h-4 text-emerald-500 ml-auto" />
      ) : isConfigured ? (
        <Link2 className="w-4 h-4 text-slate-400 ml-auto" />
      ) : (
        <AlertCircle className="w-3.5 h-3.5 text-slate-400 ml-auto" />
      )}
    </div>
  );
};

export function IntegrationsTab() {
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Fetch statuses
  useEffect(() => {
    let isMounted = true;
    
    const fetchStatuses = async () => {
      const newStatuses = {};
      
      await Promise.all(
        PROVIDERS.map(async (provider) => {
          try {
            const response = await fetch(`${backendUrl}/api/integrations/${provider.id}/status`);
            if (response.ok) {
              const data = await response.json();
              newStatuses[provider.id] = {
                configured: data.configured,
                connected: data.connected || false
              };
            }
          } catch (error) {
            newStatuses[provider.id] = { configured: false, connected: false };
          }
        })
      );
      
      if (isMounted) {
        setStatuses(newStatuses);
        setLoading(false);
      }
    };
    
    fetchStatuses();
    return () => { isMounted = false; };
  }, []);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    const newStatuses = {};
    
    await Promise.all(
      PROVIDERS.map(async (provider) => {
        try {
          const response = await fetch(`${backendUrl}/api/integrations/${provider.id}/status`);
          if (response.ok) {
            const data = await response.json();
            newStatuses[provider.id] = {
              configured: data.configured,
              connected: data.connected || false
            };
          }
        } catch (error) {
          newStatuses[provider.id] = { configured: false, connected: false };
        }
      })
    );
    
    setStatuses(newStatuses);
    setRefreshing(false);
  };
  
  // Calculate stats
  const connectedCount = Object.values(statuses).filter(s => s.connected).length;
  const configuredCount = Object.values(statuses).filter(s => s.configured).length;
  const connectedProviders = PROVIDERS.filter(p => statuses[p.id]?.connected);
  const availableProviders = PROVIDERS.filter(p => statuses[p.id]?.configured && !statuses[p.id]?.connected);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-0">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Integration Hub
              </h3>
              <p className="text-indigo-100 text-sm mt-1">
                {connectedCount} connected • {configuredCount} available
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="secondary"
                onClick={handleRefresh}
                disabled={refreshing}
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button 
                size="sm" 
                variant="secondary"
                asChild
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <Link to="/integration-hub">
                  Manage All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Connected Integrations */}
      {connectedProviders.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" />
              Connected ({connectedProviders.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {connectedProviders.map(provider => (
                <ProviderBadge 
                  key={provider.id} 
                  provider={provider} 
                  status={statuses[provider.id]} 
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Available to Connect */}
      {availableProviders.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="w-4 h-4 text-slate-500" />
              Ready to Connect ({availableProviders.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {availableProviders.map(provider => (
                <ProviderBadge 
                  key={provider.id} 
                  provider={provider} 
                  status={statuses[provider.id]} 
                />
              ))}
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              asChild
            >
              <Link to="/integration-hub">
                Connect Your Business Apps
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Empty State */}
      {connectedProviders.length === 0 && availableProviders.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="pt-6 text-center">
            <Zap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-900">No Integrations Available</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">
              Configure integrations in the admin panel to enable connections.
            </p>
            <Button variant="outline" asChild>
              <Link to="/integration-hub">
                Go to Integration Hub
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Quick Category Overview */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.entries(CATEGORIES).map(([key, cat]) => {
          const categoryProviders = PROVIDERS.filter(p => p.category === key);
          const connectedInCategory = categoryProviders.filter(p => statuses[p.id]?.connected).length;
          const Icon = cat.icon;
          
          const colorClasses = {
            emerald: 'bg-emerald-100 text-emerald-600',
            purple: 'bg-purple-100 text-purple-600',
            pink: 'bg-pink-100 text-pink-600',
            red: 'bg-red-100 text-red-600',
            indigo: 'bg-indigo-100 text-indigo-600',
            amber: 'bg-amber-100 text-amber-600',
            cyan: 'bg-cyan-100 text-[#0066CC]',
            slate: 'bg-slate-100 text-slate-600',
            green: 'bg-green-100 text-green-600',
            blue: 'bg-blue-100 text-blue-600'
          };
          
          return (
            <Card key={key} className="text-center p-3">
              <div className={`w-10 h-10 rounded-lg ${colorClasses[cat.color]} flex items-center justify-center mx-auto mb-2`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-xs font-medium text-slate-900">{cat.name}</p>
              <p className="text-[10px] text-slate-500">
                {connectedInCategory}/{categoryProviders.length} connected
              </p>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default IntegrationsTab;
